import * as Types from '@aeppic/types'

import type { NamedExports } from './types.js'

interface LookupPackageFile {
  (packageReference: Types.Reference, relativeFilePath: string): {
    fileUrl: string;
    nonDottedRelativeFilePath: string;
  }
}

type FetchFunction = typeof fetch

export interface ImportModulePackageFileOptions {
  packageReference: Types.Reference
  filePath: string
  lookupPackageFile: LookupPackageFile
  fetch: FetchFunction
  importFunction: (uri: string) => Promise<unknown>
  builtinModules: NamedExports
}

const RUNTIME_MODULES_GLOBAL = '__aeppicPackageRuntimeModules'
const STATIC_MODULE_SPECIFIER_PATTERN = /\b(?:import|export)\s+(?:(?:[^'";]+?\s*from\s*)?)(['"])([^'"]+)\1/g
const DYNAMIC_IMPORT_SPECIFIER_PATTERN = /\bimport\s*\(\s*(['"])([^'"]+)\1\s*\)/g
const VALID_EXPORT_NAME_PATTERN = /^[A-Za-z_$][\w$]*$/

export async function importModulePackageFile(options: ImportModulePackageFileOptions): Promise<unknown> {
  const importer = new PackageModuleImporter(options)
  return importer.import()
}

class PackageModuleImporter {
  private _packageReference: Types.Reference
  private _filePath: string
  private _lookupPackageFile: LookupPackageFile
  private _fetch: FetchFunction
  private _importFunction: (uri: string) => Promise<unknown>
  private _builtinModules: NamedExports
  private _sourceModuleUrlCache = new Map<string, Promise<string>>()
  private _builtinModuleUrlCache = new Map<string, string>()

  constructor({ packageReference, filePath, lookupPackageFile, fetch: fetchFunction, importFunction, builtinModules }: ImportModulePackageFileOptions) {
    this._packageReference = packageReference
    this._filePath = filePath
    this._lookupPackageFile = lookupPackageFile
    this._fetch = fetchFunction
    this._importFunction = importFunction
    this._builtinModules = builtinModules
  }

  async import(): Promise<unknown> {
    const moduleUrl = await this._loadSourceModuleUrl(this._filePath)
    return this._importFunction(moduleUrl)
  }

  private _loadSourceModuleUrl(relativeFilePath: string): Promise<string> {
    const normalizedFilePath = this._normalizePackageRelativePath(relativeFilePath)
    const cacheKey = `${this._packageReference.id}@${this._packageReference.v}/${normalizedFilePath}`

    let moduleUrlPromise = this._sourceModuleUrlCache.get(cacheKey)

    if (!moduleUrlPromise) {
      moduleUrlPromise = this._createSourceModuleUrl(normalizedFilePath)
      this._sourceModuleUrlCache.set(cacheKey, moduleUrlPromise)
    }

    return moduleUrlPromise
  }

  private async _createSourceModuleUrl(relativeFilePath: string): Promise<string> {
    const { fileUrl } = this._lookupPackageFile(this._packageReference, relativeFilePath)
    const response = await this._fetch(fileUrl)

    if (!response.ok) {
      throw new Error(`File ${fileUrl} could not be fetched (${response.statusText})`)
    }

    const source = await response.text()
    const rewrittenSource = await this._rewriteModuleSpecifiers(source, relativeFilePath)
    return createJavaScriptModuleUrl(rewrittenSource, fileUrl)
  }

  private async _rewriteModuleSpecifiers(source: string, relativeFilePath: string): Promise<string> {
    let rewrittenSource = await replaceModuleSpecifier(source, STATIC_MODULE_SPECIFIER_PATTERN, async specifier => {
      return this._resolveModuleSpecifier(specifier, relativeFilePath)
    })

    rewrittenSource = await replaceModuleSpecifier(rewrittenSource, DYNAMIC_IMPORT_SPECIFIER_PATTERN, async specifier => {
      return this._resolveModuleSpecifier(specifier, relativeFilePath)
    })

    return rewrittenSource
  }

  private async _resolveModuleSpecifier(specifier: string, relativeFilePath: string): Promise<string> {
    if (Object.prototype.hasOwnProperty.call(this._builtinModules, specifier)) {
      return this._getBuiltinModuleUrl(specifier)
    }

    if (isRelativeModuleSpecifier(specifier)) {
      const resolvedPackageRelativePath = this._resolvePackageRelativePath(relativeFilePath, specifier)
      return this._loadSourceModuleUrl(resolvedPackageRelativePath)
    }

    return specifier
  }

  private _getBuiltinModuleUrl(specifier: string): string {
    let moduleUrl = this._builtinModuleUrlCache.get(specifier)

    if (!moduleUrl) {
      moduleUrl = createJavaScriptModuleUrl(
        createBuiltinModuleSource(specifier, this._builtinModules[specifier]),
        `aeppic-runtime-module:${specifier}`,
      )
      this._builtinModuleUrlCache.set(specifier, moduleUrl)
    }

    return moduleUrl
  }

  private _resolvePackageRelativePath(fromRelativeFilePath: string, importedSpecifier: string) {
    const fromUrl = new URL(this._normalizePackageRelativePath(fromRelativeFilePath), 'package://aeppic/')
    const resolvedUrl = new URL(importedSpecifier, fromUrl)

    return `./${resolvedUrl.pathname.replace(/^\//, '')}`
  }

  private _normalizePackageRelativePath(relativeFilePath: string) {
    if (relativeFilePath.startsWith('./')) {
      return relativeFilePath
    }

    return `./${relativeFilePath}`
  }
}

async function replaceModuleSpecifier(
  source: string,
  pattern: RegExp,
  resolveSpecifier: (specifier: string) => Promise<string>,
): Promise<string> {
  const chunks = []
  let lastIndex = 0

  for (const match of source.matchAll(pattern)) {
    const matchStartIndex = match.index

    if (matchStartIndex == null) {
      continue
    }

    const [matchedText, quote, specifier] = match
    const resolvedSpecifier = await resolveSpecifier(specifier)
    const quotedSpecifier = `${quote}${specifier}${quote}`
    const specifierStartInMatch = matchedText.lastIndexOf(quotedSpecifier)

    if (specifierStartInMatch === -1) {
      continue
    }

    chunks.push(source.slice(lastIndex, matchStartIndex + specifierStartInMatch))
    chunks.push(`${quote}${resolvedSpecifier}${quote}`)
    lastIndex = matchStartIndex + specifierStartInMatch + quotedSpecifier.length
  }

  chunks.push(source.slice(lastIndex))

  return chunks.join('')
}

function createBuiltinModuleSource(specifier: string, runtimeModule: unknown): string {
  const registry = getRuntimeModuleRegistry()
  const moduleKey = `runtime:${specifier}:${registry.size}`
  registry.set(moduleKey, runtimeModule)

  const namedExportStatements = Array.from(new Set(Object.getOwnPropertyNames(Object(runtimeModule))))
    .filter(exportName => exportName !== 'default' && VALID_EXPORT_NAME_PATTERN.test(exportName))
    .map((exportName, index) => {
      const localName = `__aeppicRuntimeExport${index}`
      return `const ${localName} = __aeppicRuntimeDefault[${JSON.stringify(exportName)}]\nexport { ${localName} as ${exportName} }`
    })

  return [
    `const __aeppicRuntimeModule = globalThis.${RUNTIME_MODULES_GLOBAL}.get(${JSON.stringify(moduleKey)})`,
    'const __aeppicRuntimeDefault = __aeppicRuntimeModule?.default ?? __aeppicRuntimeModule',
    'export default __aeppicRuntimeDefault',
    ...namedExportStatements,
  ].join('\n')
}

function getRuntimeModuleRegistry(): Map<string, unknown> {
  const global = <typeof globalThis & { [RUNTIME_MODULES_GLOBAL]?: Map<string, unknown> }>globalThis

  if (!global[RUNTIME_MODULES_GLOBAL]) {
    global[RUNTIME_MODULES_GLOBAL] = new Map<string, unknown>()
  }

  return global[RUNTIME_MODULES_GLOBAL]
}

function createJavaScriptModuleUrl(source: string, sourceUrl: string) {
  const sourceWithHint = `${source}\n//# sourceURL=${sourceUrl}`

  if (canUseBlobModuleUrls()) {
    return URL.createObjectURL(new Blob([sourceWithHint], { type: 'text/javascript' }))
  }

  return `data:text/javascript;charset=utf-8;base64,${encodeBase64(sourceWithHint)}`
}

function canUseBlobModuleUrls() {
  return typeof window !== 'undefined'
    && typeof Blob !== 'undefined'
    && typeof URL.createObjectURL === 'function'
}

function encodeBase64(source: string) {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(source, 'utf8').toString('base64')
  }

  const bytes = new TextEncoder().encode(source)
  let binary = ''

  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }

  return btoa(binary)
}

function isRelativeModuleSpecifier(specifier: string) {
  return specifier.startsWith('./') || specifier.startsWith('../')
}
